import * as Sentry from '@sentry/react';
import isProd from '../utils/isProd';
import { isQA } from '../utils/envUtils';
import { getVersion } from '../utils/getVersion';
import { getMerchantId } from '../utils/merchant';

/**
 * Single place where this application's Sentry behaviour is decided.
 *
 * The DSN is a public write-only key and has always been in the source; an
 * auth token is not and never belongs in this file. Source-map upload
 * credentials live only in CI. See docs/plans/observability-sentry-baseline.md.
 */
export const SENTRY_DSN =
  'https://eeca1d7a7798474f93526af4d5ccdd66@o378721.ingest.sentry.io/4504951137304576';

/**
 * Which Sentry project this build reports to.
 *
 * `REACT_APP_SENTRY_DSN` overrides; `SENTRY_DSN` above stays the default, so
 * a build that sets nothing resolves the same DSN it resolved before
 * ECOM-3401. That ordering is the whole point of the change: replacing the
 * literal instead of defaulting to it would mean an unconfigured deploy
 * passes `undefined` to `Sentry.init`, which stops reporting silently and
 * undoes ECOM-3400 with nothing in any log to say so.
 *
 * A blank value counts as not set and falls back, for the same reason:
 * `REACT_APP_SENTRY_DSN=` in a Netlify site is someone leaving a field empty,
 * not someone asking for error reporting to be switched off. Whitespace is
 * trimmed first, so a value pasted with a trailing newline resolves to the
 * DSN rather than to an unparseable one.
 *
 * `scripts/build-env-lib.cjs` reaches for `hasOwnProperty` at the equivalent
 * point and is right to, but it is not the tool here, on two counts. Its rule
 * is the opposite one - an explicitly empty `REACT_APP_COMMIT_REF` means
 * *suppress the commit*, so presence has to beat the Netlify fallback - and
 * it runs in Node against a real `process.env` object. This runs in the
 * browser, where CRA's DefinePlugin has already replaced the member access
 * below with a string literal or with `undefined`; there is no object left to
 * ask about presence, so emptiness is the only signal available.
 *
 * Read as a literal `process.env.REACT_APP_*` member access on purpose: that
 * is the form CRA inlines. Anything else compiles to undefined.
 */
export function resolveDsn() {
  const override = process.env.REACT_APP_SENTRY_DSN;
  const configured = typeof override === 'string' ? override.trim() : '';
  return configured === '' ? SENTRY_DSN : configured;
}

/**
 * Performance transactions, not errors. Errors and the replays attached to
 * them are unaffected by this rate. 0.1 keeps a representative sample of page
 * loads and navigations; reverting to 1.0 is a one-line change here.
 */
export const TRACES_SAMPLE_RATE = 0.1;

/** Never record a replay just because a session started. */
export const REPLAYS_SESSION_SAMPLE_RATE = 0;

/** Record a replay for a small share of sessions that produced an error. */
export const REPLAYS_ON_ERROR_SAMPLE_RATE = 0.05;

export const REDACTED = '[redacted]';

/** Tag keys this module owns, so clearing them is exhaustive and auditable. */
export const STORE_TAG_KEYS = ['store.id', 'store.uuid', 'merchant.id'];

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]*\w/g;
const FORMATTED_PHONE_PATTERN =
  /(?:\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
const LONG_DIGIT_RUN_PATTERN = /\d{7,}/g;

/**
 * Breadcrumb categories whose payloads routinely carry rendered customer data.
 * Console breadcrumbs in particular capture whatever ErrorDamper logged, which
 * is the whole error object of a customer or order request.
 */
const DROPPED_BREADCRUMB_CATEGORIES = ['console', 'ui.input'];

const URL_BREADCRUMB_FIELDS = ['url', 'to', 'from'];

/**
 * Netlify deploy contexts that correspond to something actually deployed.
 * `dev` is Netlify's local `netlify dev` and is not one of them.
 * scripts/build-env-lib.cjs is what puts the value in the bundle.
 */
export const DEPLOYED_CONTEXTS = [
  'production',
  'deploy-preview',
  'branch-deploy',
];

/** What a build that someone deployed may report. */
export const DEPLOYED_ENVIRONMENTS = [
  'production',
  'staging',
  'preview',
  'branch-deploy',
];

/**
 * Everything resolveEnvironment() can return. There is deliberately no
 * `unknown` here: it used to be the fall-through for any build whose API URL
 * neither helper recognised, which silently put every deploy preview and
 * every branch deploy into a bucket that named no environment at all.
 */
export const SENTRY_ENVIRONMENTS = [
  ...DEPLOYED_ENVIRONMENTS,
  'development',
  'local-build',
];

/**
 * Which environment this build belongs to, decided from two signals rather
 * than one.
 *
 * The deploy context comes first, because it is the only thing that separates
 * a preview or a branch deploy from the staging site: all three talk to the
 * same API, so REACT_APP_API_URL cannot tell them apart and used to lump them
 * together. The API URL then separates production from staging among the
 * published sites, which is what it has always been good at - both staging
 * sites publish `develop` as their own production context, so the context
 * alone cannot answer that.
 *
 * Every branch returns a named environment. The last one is reachable only
 * from a build made outside Netlify, which is exactly what it says.
 */
export function resolveEnvironment() {
  const context = process.env.REACT_APP_DEPLOY_CONTEXT;

  if (context === 'deploy-preview') {
    return 'preview';
  }
  if (context === 'branch-deploy') {
    return 'branch-deploy';
  }
  if (isProd()) {
    return 'production';
  }
  if (isQA()) {
    return 'staging';
  }
  if (context === 'production') {
    // A published Netlify site that is not the production API is a staging
    // site, whatever its API URL happens to be spelled as today.
    return 'staging';
  }
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  return 'local-build';
}

/**
 * The release name must match what CI uploads source maps under, otherwise a
 * stack trace cannot be mapped back to the build that produced it.
 * scripts/sentry-release-lib.cjs derives the same string from package.json,
 * and its test reads this constant out of this file to prove it still does.
 */
export const RELEASE_PREFIX = 'mission-control@';

export function resolveRelease() {
  return `${RELEASE_PREFIX}${getVersion()}`;
}

/**
 * The version is derived from git at build time since ECOM-3397 and ADR 0018,
 * and is CalVer since ADR 0019 - `2026.11.02.1` on master and
 * `2026.11.02.1-rc` on develop, the day and the commit's position in it - so
 * two builds of different commits no longer share a release name. `dist` is
 * Sentry's second half of a build's identity: the commit, which moves with
 * every deploy. Artifacts are uploaded under the same release and dist, so
 * both must be derived the same way - see scripts/build-env-lib.cjs.
 *
 * The pair is what identifies a build, which is why a version shape shared by
 * two branches would still not confuse two builds: their commits differ, so
 * their dists differ, and a stack trace resolves only against the artifacts
 * uploaded under its own dist.
 *
 * Read as a literal `process.env.REACT_APP_*` member access on purpose: that
 * is the form CRA inlines. Anything else compiles to undefined.
 */
export function resolveDist() {
  return process.env.REACT_APP_COMMIT_REF || undefined;
}

/** Redact the PII shapes this admin renders: emails, phones, long id runs. */
export function scrubText(value) {
  if (typeof value !== 'string') {
    return value;
  }
  return value
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(FORMATTED_PHONE_PATTERN, REDACTED)
    .replace(LONG_DIGIT_RUN_PATTERN, REDACTED);
}

/**
 * Query strings carry `filter[email]`, `filter[phone]` and the search terms
 * staff type against customer records; fragments carry the same. Both go. The
 * path stays so the route is still identifiable, with id runs redacted.
 */
export function scrubUrl(value) {
  if (typeof value !== 'string') {
    return value;
  }
  return scrubText(value.split('#')[0].split('?')[0]);
}

function scrubBreadcrumb(breadcrumb) {
  if (DROPPED_BREADCRUMB_CATEGORIES.includes(breadcrumb.category)) {
    return null;
  }

  const scrubbed = { ...breadcrumb, message: scrubText(breadcrumb.message) };

  if (scrubbed.data) {
    const data = { ...scrubbed.data };
    URL_BREADCRUMB_FIELDS.forEach((field) => {
      if (field in data) {
        data[field] = scrubUrl(data[field]);
      }
    });
    scrubbed.data = data;
  }

  return scrubbed;
}

function scrubRequest(request) {
  const scrubbed = { ...request, url: scrubUrl(request.url) };
  // Bodies hold customer payloads, headers hold the bearer token and X-Store,
  // cookies hold the session, and the query string is already folded into the
  // scrubbed `url` above.
  delete scrubbed.query_string;
  delete scrubbed.data;
  delete scrubbed.headers;
  delete scrubbed.cookies;
  return scrubbed;
}

function scrubException(exception) {
  if (!exception.values) {
    return exception;
  }
  return {
    ...exception,
    values: exception.values.map((value) => ({
      ...value,
      value: scrubText(value.value),
    })),
  };
}

/**
 * beforeSend. Everything here runs in the browser, before the event leaves it.
 */
export function scrubEvent(event) {
  if (!event) {
    return event;
  }

  const scrubbed = { ...event };

  if (scrubbed.request) {
    scrubbed.request = scrubRequest(scrubbed.request);
  }

  if (scrubbed.user) {
    // The logged-in staff member stays identifiable for triage; their network
    // location does not need to be.
    scrubbed.user = { ...scrubbed.user, ip_address: null };
  }

  if (scrubbed.message) {
    scrubbed.message = scrubText(scrubbed.message);
  }

  if (scrubbed.exception) {
    scrubbed.exception = scrubException(scrubbed.exception);
  }

  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs
      .map(scrubBreadcrumb)
      .filter(Boolean);
  }

  // Nothing in this app sets `extra` deliberately, so anything arriving here
  // is unreviewed and is dropped rather than inspected.
  delete scrubbed.extra;

  return scrubbed;
}

export function buildSentryOptions() {
  return {
    dsn: resolveDsn(),
    release: resolveRelease(),
    dist: resolveDist(),
    environment: resolveEnvironment(),
    integrations: [
      new Sentry.BrowserTracing(),
      new Sentry.Replay({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
      }),
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
    sendDefaultPii: false,
    beforeSend: scrubEvent,
  };
}

export function initSentry() {
  if (process.env.NODE_ENV === 'development') {
    return false;
  }
  Sentry.init(buildSentryOptions());
  return true;
}

/**
 * Report a failure the UI has already handled (ECOM-3451).
 *
 * **A caught error is still an error.** Everything Sentry sees today arrives
 * through `Sentry.ErrorBoundary` or the global handlers, so a failure the app
 * catches and draws as a red box plus a toast -- which is what every beta list
 * does when its request fails -- left no trace anywhere but one merchant's
 * console. Settings › Users broke on staging for an afternoon with nothing in
 * Sentry to find.
 *
 * Nothing here changes the release name: `resolveRelease()` is untouched, so
 * events still land under the release CI uploads source maps for.
 *
 * `beforeSend` (`scrubEvent`) still runs, so the message is scrubbed of
 * emails, phone numbers and long id runs before the event leaves the browser.
 * Tags only -- `scrubEvent` deletes `extra` by design, and tags must be
 * strings.
 *
 * **It cannot throw.** This is called from a render effect on a screen that
 * is already failing; a reporter that took that screen down would be the
 * exact irony. Anything Sentry raises stays here.
 *
 * @param {*} error whatever was caught; need not be an `Error`
 * @param {Object} [tags] string tags to triage by, e.g. `{ resource }`
 * @returns {void}
 */
export function captureHandledError(error, tags = {}) {
  const reportable =
    error instanceof Error
      ? error
      : new Error(
          typeof error === 'string' && error ? error : 'Non-error rejection'
        );

  const stringTags = {};
  Object.entries({ handled: 'true', ...tags }).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    stringTags[key] = String(value);
  });

  try {
    Sentry.captureException(reportable, { tags: stringTags });
  } catch (reportingFailure) {
    // eslint-disable-next-line no-console
    console.error(
      '[sentry] could not report a handled error',
      reportingFailure
    );
  }
}

function readMerchantId(store) {
  if (!store.pos_tenancy) {
    return null;
  }
  return getMerchantId(store);
}

/**
 * Which store an error happened in is the dimension this product is triaged
 * by, and a stale tag is worse than none: every path through here writes all
 * three keys, so changing store can never leave the previous store's tags on.
 */
export function setStoreTags(store) {
  if (!store) {
    STORE_TAG_KEYS.forEach((key) => Sentry.setTag(key, undefined));
    return;
  }

  Sentry.setTag('store.id', store.id == null ? undefined : String(store.id));
  Sentry.setTag('store.uuid', store.uuid || undefined);
  Sentry.setTag('merchant.id', readMerchantId(store) || undefined);
}
